import React, { useRef, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Camera } from 'lucide-react';
import useFaceDetection from '../../../hooks/useFaceDetection';
import { useWebSocket } from '../../../WebSocketContext';
import logger from './../../../utils/logger.js';


const CameraStep = ({ permissionStatus, setPermissionStatus, checkCameraPermission }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [permissionSuccessSent, setPermissionSuccessSent] = useState(false);
  
  // Get WebSocket context
  const { sendCameraPermissionSuccess } = useWebSocket();
  
  // Include face detection hook directly in the component
  const { faceCount, faceDetectionStatus, isDetectorReady, detections } = useFaceDetection(videoRef, canvasRef);
  
  // Update parent component's permission status when face detection status changes
  useEffect(() => {
    logger.debug("Face detection status changed in CameraStep:", faceDetectionStatus);
    if (faceDetectionStatus === 'face_detected') {
      logger.debug("Setting face detection permission to granted");
      setPermissionStatus(prev => ({ ...prev, faceDetection: 'granted' }));
      
      // Send camera permission success event when face is detected
      if (!permissionSuccessSent) {
        sendCameraPermissionSuccess();
        setPermissionSuccessSent(true);
      }
    } else if (faceDetectionStatus === 'no_face') {
      logger.debug("Setting face detection permission to waiting");
      setPermissionStatus(prev => ({ ...prev, faceDetection: 'waiting' }));
    } else if (faceDetectionStatus === 'multiple_faces') {
      logger.debug("Setting face detection permission to warning");
      setPermissionStatus(prev => ({ ...prev, faceDetection: 'warning' }));
    }
  }, [faceDetectionStatus, setPermissionStatus, sendCameraPermissionSuccess, permissionSuccessSent]);
  
  // Initialize camera when component mounts
  useEffect(() => {
    const initializeCamera = async () => {
      try {
        logger.debug('Initializing camera in CameraStep component');
        
        // Try with the most compatible constraints first for Android devices
        const constraints = {
          video: {
            facingMode: 'user',
          },
          audio: false
        };
        
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          setCameraStream(stream);
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            logger.debug('Camera stream set to video element');
          }
        } catch (initialError) {
          logger.error('Error with initial camera constraints, trying fallback:', initialError);
          
          // Fallback to more specific constraints if the first attempt fails
          const fallbackConstraints = {
            video: true,
            audio: false
          };
          
          const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          setCameraStream(stream);
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            logger.debug('Camera stream set to video element with fallback constraints');
          }
        }
      } catch (error) {
        logger.error('Error accessing camera:', error);
      }
    };
    
    // Only initialize if permission is pending or granted
    if (permissionStatus.camera !== 'denied') {
      initializeCamera();
    }
    
    // Cleanup function to stop camera stream when component unmounts
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        logger.debug('Camera stream stopped');
      }
    };
  }, [permissionStatus.camera]);
  
  return (
    <div className="flex flex-col w-full justify-center items-center">
      <h2 className="text-md font-semibold text-gray-900 ">Camera & Face Detection Check</h2>
      
      <p className="text-sm text-gray-600 mb-4 text-md mb-2 text-center">We need to verify your camera works and can detect your face properly.</p>
      
      {permissionStatus.camera === 'granted' ? (
        <div className="w-full max-w-2xl mx-auto mb-2">
          <div className="relative rounded-lg overflow-hidden" style={{ maxHeight: 'calc(50vh - 50px)' }}>
            <video 
              ref={videoRef}
              autoPlay 
              playsInline 
              muted
              className={`w-full h-auto object-cover max-h-[50vh] ${faceDetectionStatus === 'face_detected' ? 'border-4 border-green-500' : faceDetectionStatus === 'multiple_faces' ? 'border-4 border-yellow-500' : 'border border-gray-300'}`}
              onLoadedMetadata={() => {
                // Ensure canvas is properly sized once video metadata is loaded
                if (videoRef.current && canvasRef.current) {
                  const videoRect = videoRef.current.getBoundingClientRect();
                  canvasRef.current.width = videoRect.width;
                  canvasRef.current.height = videoRect.height;
                  logger.debug('Canvas size set to match video dimensions:', videoRect.width, 'x', videoRect.height);
                }
              }}
            />
            <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full" />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white py-1 px-3 text-xs sm:text-sm">
              {!isDetectorReady ? 'Initializing face detector...' : 
                faceDetectionStatus === 'face_detected' ? 'Face detected ✓' :
                faceDetectionStatus === 'multiple_faces' ? `Multiple faces (${faceCount}) ⚠️` :
                `No face detected ⚠️`
              }
            </div>
          </div>
          
          <div className="mt-2">
            {!isDetectorReady && (
              <div className="flex items-center p-2 bg-blue-50 text-blue-700 rounded-md text-xs sm:text-sm">
                <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
                <span>Initializing face detection, please wait...</span>
              </div>
            )}
            {isDetectorReady && faceDetectionStatus === 'no_face' && (
              <div className="flex items-center p-2 bg-yellow-50 text-yellow-700 rounded-md text-xs sm:text-sm">
                <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
                <span>No face detected. Please position yourself in front of the camera.</span>
              </div>
            )}
            {isDetectorReady && faceDetectionStatus === 'multiple_faces' && (
              <div className="flex items-center p-2 bg-yellow-50 text-yellow-700 rounded-md text-xs sm:text-sm">
                <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
                <span>Multiple faces detected. Only one person should be visible.</span>
              </div>
            )}
            {isDetectorReady && faceDetectionStatus === 'face_detected' && (
              <div className="flex items-center p-2 bg-green-50 text-green-700 rounded-md text-xs sm:text-sm">
                <CheckCircle size={18} className="mr-2 flex-shrink-0" />
                <span>Face detected! You're good to go.</span>
              </div>
            )}
          </div>
        </div>
      ) : permissionStatus.camera === 'denied' ? (
        <div className="p-3 bg-red-50 text-red-700 rounded-md">
          <p className="mb-3">Camera access denied. Please allow access in your browser settings and try again.</p>
          <button 
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            onClick={checkCameraPermission}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
          <div className="mb-3 p-3 bg-blue-100 rounded-full">
            <Camera size={40} className="text-blue-600" />
          </div>
          <p className="text-center mb-3 text-sm">Please allow access to your camera when prompted by your browser.</p>
          {permissionStatus.camera === 'pending' && (
            <button 
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              onClick={checkCameraPermission}
            >
              Check Camera
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CameraStep;